import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getSettings,
  updateSettings,
} from "../../../../../../../services/settingsServices";

import {
  uploadReglementHeaderImage,
  uploadReglementFirstImage,
  uploadReglementSecondImage,
  toAbsolutePhotoUrl,
} from "../../../../../../../services/settingsPhotoServices";

import {
  DEFAULT_REGLEMENT_SETTINGS,
  REGLEMENT_FALLBACK_IMAGE,
  REGLEMENT_UPLOAD_TARGETS,
  normalizeReglementPayload,
  unwrapSettings,
  validateImageFile,
  withBust,
} from "../utils/reglementInterieurUtils";

const uploaders = {
  header: uploadReglementHeaderImage,
  first: uploadReglementFirstImage,
  second: uploadReglementSecondImage,
};

export function useReglementInterieurSettings() {
  const [settings, setSettings] = useState(DEFAULT_REGLEMENT_SETTINGS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [uploadingTarget, setUploadingTarget] = useState(null);
  const uploading = Boolean(uploadingTarget);

  const [selectedFiles, setSelectedFiles] = useState({
    header: null,
    first: null,
    second: null,
  });

  const [imgVersion, setImgVersion] = useState({
    header: 0,
    first: 0,
    second: 0,
  });

  const [previewDark, setPreviewDark] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getSettings();
      const first = unwrapSettings(data);

      if (first) {
        setSettings({
          ...DEFAULT_REGLEMENT_SETTINGS,
          ...first,
        });

        setImgVersion((prev) => ({
          header: prev.header + 1,
          first: prev.first + 1,
          second: prev.second + 1,
        }));
      }
    } catch (error) {
      console.error("Erreur chargement règlement intérieur :", error);
      toast.error("Impossible de charger le règlement intérieur.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const previewUrls = useMemo(() => {
    const headerRaw = settings.reglement_interieur_url_header?.trim() || "";
    const firstRaw = settings.reglement_interieur_url_firstimage?.trim() || "";
    const secondRaw = settings.reglement_interieur_url_secondimage?.trim() || "";

    const headerAbs = toAbsolutePhotoUrl(headerRaw);
    const firstAbs = toAbsolutePhotoUrl(firstRaw);
    const secondAbs = toAbsolutePhotoUrl(secondRaw);

    return {
      header: withBust(headerAbs || headerRaw || REGLEMENT_FALLBACK_IMAGE, imgVersion.header),
      first: withBust(firstAbs || firstRaw || REGLEMENT_FALLBACK_IMAGE, imgVersion.first),
      second: withBust(secondAbs || secondRaw || REGLEMENT_FALLBACK_IMAGE, imgVersion.second),
    };
  }, [
    settings.reglement_interieur_url_header,
    settings.reglement_interieur_url_firstimage,
    settings.reglement_interieur_url_secondimage,
    imgVersion.header,
    imgVersion.first,
    imgVersion.second,
  ]);

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    try {
      setSaving(true);

      const payload = normalizeReglementPayload(settings);

      await updateSettings(payload);

      setSettings(payload);
      toast.success("Règlement intérieur mis à jour avec succès.");
    } catch (error) {
      console.error("Erreur sauvegarde règlement intérieur :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de la mise à jour du règlement intérieur."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (target, file) => {
    if (!REGLEMENT_UPLOAD_TARGETS[target]) return;

    if (!file) {
      setSelectedFiles((prev) => ({
        ...prev,
        [target]: null,
      }));
      return;
    }

    const errorMessage = validateImageFile(file);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setSelectedFiles((prev) => ({
      ...prev,
      [target]: file,
    }));
  };

  const clearSelectedFile = (target) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [target]: null,
    }));
  };

  const handleUploadImage = async (target) => {
    const targetConfig = REGLEMENT_UPLOAD_TARGETS[target];
    const uploadFn = uploaders[target];
    const file = selectedFiles[target];

    if (!targetConfig || !uploadFn) {
      toast.error("Cible d’upload inconnue.");
      return;
    }

    const errorMessage = validateImageFile(file);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setUploadingTarget(target);

      const data = await uploadFn(file, {
        format: "webp",
      });

      const newUrl = data?.url || data?.imageUrl || "";

      if (!newUrl) {
        toast.error("Upload terminé, mais URL manquante dans la réponse.");
        return;
      }

      setSettings((prev) => ({
        ...prev,
        [targetConfig.field]: newUrl,
      }));

      setSelectedFiles((prev) => ({
        ...prev,
        [target]: null,
      }));

      setImgVersion((prev) => ({
        ...prev,
        [target]: prev[target] + 1,
      }));

      toast.success(`${targetConfig.label} mise à jour avec succès.`);
    } catch (error) {
      console.error("Erreur upload règlement intérieur :", error);
      toast.error("Erreur lors de l’upload de l’image.");
    } finally {
      setUploadingTarget(null);
    }
  };

  return {
    settings,
    loading,
    saving,

    previewUrls,
    previewDark,
    setPreviewDark,

    selectedFiles,
    uploadingTarget,
    uploading,

    fetchSettings,
    handleContentChange,
    handleSubmit,

    handleFileSelect,
    clearSelectedFile,
    handleUploadImage,
  };
}